import React from 'react';
import styled from 'styled-components';
import { divisionColors, warmGrey } from '@mergermarket/acuris-react-styles';

const SVG = ({
  title,
  width,
  height,
  className,
  removeIconClassName,
  children
}) => /*#__PURE__*/React.createElement("svg", {
  className: `${removeIconClassName ? '' : 'icon'} ${className ? className : ''}`,
  width: "100%",
  height: "100%",
  viewBox: `0 0 ${width} ${height}`,
  role: "img",
  xmlns: "http://www.w3.org/2000/svg"
}, /*#__PURE__*/React.createElement("title", null, title), children);

function makeIconComponent({
  name,
  content,
  width,
  height
}) {
  const Component = ({
    title = `${name} Icon`,
    className,
    removeIconClassName,
    ...props
  }) => /*#__PURE__*/React.createElement(SVG, {
    title: title,
    className: className,
    removeIconClassName: removeIconClassName,
    width: props.width || width,
    height: props.height || height
  }, content);

  if (browserCanChangeComponentName(Component)) {
    setNameOfComponent(Component, `${name}Icon`);
  }

  return Component;
} // Some older browsers (e.g. iOS 9) are not allowing the component name to be reconfigured.
// As this is only really for debugging/React code readability, we can safely disable it for them.

function browserCanChangeComponentName(Component) {
  const componentName = Object.getOwnPropertyDescriptor(Component, 'name');
  return componentName && componentName.configurable;
}

function setNameOfComponent(Component, name) {
  Object.defineProperty(Component, 'name', {
    value: name,
    writable: false
  });
}

const ArrowIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M2.00111214 2 0 4 6.00333641 10 12 4 10 2 6.00333641 6z",
    fillRule: "evenodd"
  }),
  name: 'Arrow',
  width: 12,
  height: 12
});
const ThinArrowIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", {
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("rect", {
    x: "7.84436",
    y: "11.5635",
    width: "0.997168",
    height: "8",
    transform: "rotate(-135 7.84436 11.5635)"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "7.84436",
    y: "11.5635",
    width: "8",
    height: "1",
    transform: "rotate(-135 7.84436 11.5635)"
  })),
  name: 'ThinArrow',
  width: 16,
  height: 16
});
const MediumArrowIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M0.705384 6.70536C1.09466 7.09463 1.72569 7.09497 2.11538 6.70613L6 2.82997L9.88462 6.70613C10.2743 7.09497 10.9053 7.09463 11.2946 6.70536C11.6842 6.31578 11.6842 5.68416 11.2946 5.29459L6.70711 0.707079C6.31658 0.316555 5.68342 0.316555 5.29289 0.707079L0.705384 5.29459C0.315811 5.68416 0.315811 6.31578 0.705384 6.70536Z"
  }),
  name: 'ThickArrow',
  width: 12,
  height: 7
});
const BellIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M20.43 5.232a2.79 2.79 0 0 1-1.383-2.414v-.004C19.049 1.26 17.805 0 16.266 0c-1.535 0-2.781 1.26-2.781 2.814v.004a2.785 2.785 0 0 1-1.381 2.414C5.912 8.83 9.47 20.774 3 22.884v2.32h26.53v-2.32c-6.467-2.11-2.91-14.055-9.1-17.652zm-4.164-3.907a1.328 1.328 0 1 1 0 0zm3.98 26.533c0 2.118-1.846 3.98-3.94 3.98-2.095 0-4.02-1.86-4.02-3.98h7.96z"
  }),
  name: 'Bell',
  width: 32,
  height: 32
});
const BreadcrumbChevronIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M4 6L0 2l2-2 6 6-1 1-5 5-2-2 4-4z"
  }),
  name: 'BreadcrumbChevron',
  width: 8,
  height: 12
});
const CalendarIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M8 4a1.333 1.333 0 0 1-2.667 0V1.333a1.334 1.334 0 0 1 2.667 0V4zM25.334 0C24.597 0 24 .597 24 1.333V4a1.333 1.333 0 0 0 2.666 0V1.333C26.666.597 26.068 0 25.334 0z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M32 2.667V32H0V2.667h4V4a2.67 2.67 0 0 0 2.667 2.667A2.67 2.67 0 0 0 9.333 4V2.667h13.333V4a2.67 2.67 0 0 0 2.668 2.667A2.67 2.67 0 0 0 28 4V2.667h4zm-2.666 8H2.667v18.667h26.667V10.667z"
  })),
  name: 'Calendar',
  width: 32,
  height: 32
});
const ChevronIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M11.293 0v8.566L16.597 16l-5.302 7.432V32l11.413-16L11.293 0z"
  }),
  name: 'Chevron',
  width: 32,
  height: 32
});
const CrossIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", {
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M5 12V0h2v12z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M0 5h12v2H0z"
  })),
  name: 'Cross',
  width: 12,
  height: 12
});
const DashIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M0 0h10v2H0z"
  }),
  name: 'Dash',
  width: 10,
  height: 2
});
const EmailIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M16.011,16.959L0.02,4h31.983L16.011,16.959z M8.773,14.528L0,7.418v16.678L8.773,14.528z M23.249,14.528l8.772,9.568V7.418L23.249,14.528z M21.172,16.21l-5.161,4.183L10.85,16.21L0.029,28.016h31.964L21.172,16.21z"
  }),
  name: 'Email',
  width: 32,
  height: 32
});
const HamburgerIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M0 1.822h32v5.671H0V1.822zm0 11.343h32v5.67H0v-5.67zm0 11.341h32v5.672H0v-5.672z"
  }),
  name: 'Hamburger',
  width: 32,
  height: 32
});
const HomeIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M28.676 30.425H19.45V23.42h-6.946v7.005H3.282v-12.89H-.02l16-15.96 16 15.96h-3.304v12.89z"
  }),
  name: 'Home',
  width: 32,
  height: 32
});
const InfoIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M18.667 10.667V32h-5.334V10.667h5.334zM20 4.038c0 .546-.11 1.057-.33 1.534-.219.477-.51.894-.873 1.252a4.172 4.172 0 0 1-1.278.857 3.89 3.89 0 0 1-1.57.319 3.74 3.74 0 0 1-1.531-.32 4.056 4.056 0 0 1-2.102-2.108A3.75 3.75 0 0 1 12 4.038a4.055 4.055 0 0 1 1.165-2.862 4.056 4.056 0 0 1 1.253-.857A3.74 3.74 0 0 1 15.949 0c.557 0 1.08.106 1.57.32.49.212.916.498 1.278.856.363.358.654.784.874 1.278.22.494.329 1.022.329 1.584z"
  }),
  name: 'Info',
  width: 32,
  height: 32
});
const InverseEmailIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M2.83 2.83v34.34h50.944V2.83H2.83zM0 0h56.604v40H0V0z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M54.975 2.54l1.574 2.352-28.247 18.916L.04 4.892 1.614 2.54l26.688 17.862z"
  })),
  name: 'InverseEmail',
  width: 57,
  height: 40
});
const LinkedInIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M6.64 4.333c0 1.842-1.48 3.334-3.307 3.334-1.826 0-3.306-1.492-3.306-3.334C.027 2.493 1.507 1 3.333 1 5.16 1 6.64 2.493 6.64 4.333zm.027 6H0v21.334h6.667V10.333zm10.642 0h-6.624v21.334h6.626V20.468c0-6.227 8.038-6.736 8.038 0v11.199H32V18.159c0-10.507-11.896-10.124-14.69-4.952v-2.874z"
  }),
  name: 'LinkedIn',
  width: 32,
  height: 32
});
const NarrowCrossIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M44.943 2.633L2.735 44.992.057 42.367 42.265.008z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M.057 2.633l42.208 42.359 2.678-2.625L2.735.008z"
  })),
  name: 'NarrowCross',
  width: 45,
  height: 45
});
const PrintIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M27.834 8.455V1.588H4.144v6.867H-.01v15.838h4.14v-4.33h23.704v4.33h4.154V8.455h-4.154zm-3.108 0H7.25V4.71h17.475v3.745zm4.183 3.96a.94.94 0 0 1-.94-.942.94.94 0 0 1 1.878 0 .94.94 0 0 1-.94.942zm-4.233 17.81H7.25v-7.568h17.426v7.568z"
  }),
  name: 'Print',
  width: 32,
  height: 32
});
const ScrollTopIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M1 26.28h8.03L16 17.665l6.968 8.612H31L16 7.74 1 26.28zM1 5.417h30V6.71H1z"
  }),
  name: 'ScrollTop',
  width: 32,
  height: 32
});
const SearchIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M31.75 28.866l-8.275-8.275a12.91 12.91 0 0 0 2.477-7.616C25.952 5.821 20.13 0 12.975 0 5.821 0 0 5.82 0 12.975 0 20.131 5.82 25.95 12.975 25.95c2.713 0 5.232-.836 7.317-2.264l8.319 8.319 3.139-3.14zM3.806 12.976c0-5.058 4.114-9.171 9.17-9.171 5.057 0 9.171 4.113 9.171 9.17 0 5.057-4.114 9.17-9.17 9.17-5.059 0-9.171-4.113-9.171-9.17z",
    fillRule: "nonzero"
  }),
  name: 'Search',
  width: 32,
  height: 32
});
const ShareIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M7.553 22.54c1.127 0 2.235-.29 3.217-.845.628-.353 1.4-.33 2.008.057l6.796 4.344c.525.336.857.902.895 1.523A4.666 4.666 0 0 0 25.13 32c2.58 0 4.67-2.087 4.67-4.66 0-3.302-3.333-5.527-6.368-4.342a1.947 1.947 0 0 1-1.76-.175l-5.85-3.74a3.72 3.72 0 0 1-1.716-3.11 3.568 3.568 0 0 1 1.647-3.016l5.92-3.782c.522-.334 1.18-.4 1.76-.174 3.038 1.188 6.368-1.045 6.368-4.34C29.8 2.084 27.71 0 25.13 0a4.665 4.665 0 0 0-4.663 4.378 1.937 1.937 0 0 1-.893 1.525l-6.796 4.343c-.607.386-1.38.41-2.008.056a6.533 6.533 0 0 0-3.217-.842C3.933 9.46 1 12.39 1 16s2.934 6.54 6.553 6.54z"
  }),
  name: 'Share',
  width: 32,
  height: 32
});
const TickIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M15.924 44.749l-.001.001L0 28.827 2.927 25.9l13.247 13.248L55.322 0l2.926 2.927L16.175 45l-.251-.251z",
    fillRule: "evenodd"
  }),
  name: 'Tick',
  width: 59,
  height: 45
});
const UserIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M25.333 9.335c0 5.153-4.178 9.333-9.333 9.333s-9.333-4.18-9.333-9.333a9.334 9.334 0 1 1 18.666 0zm-2.13 9.573A11.915 11.915 0 0 1 16 21.335c-2.707 0-5.199-.914-7.21-2.43C3.362 21.296 0 28.74 0 32h32c0-3.23-3.467-10.675-8.797-13.092z",
    fillRule: "nonzero"
  }),
  name: 'User',
  width: 32,
  height: 32
});
const XingIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M19.516 32l-7.099-12.89L23.177 0h6.578L18.996 19.11 26.023 32h-6.507zM9.795 6.667H3.209L6.9 12.992l-5.487 9.675h6.552l5.49-9.695-3.66-6.305z"
  }),
  name: 'Xing',
  width: 32,
  height: 32
});
const DuplicateIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero"
  }, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M7.62426 2L5 2C5 0.89543 5.89543 0 7 0H11.6243C11.8665 0 12.1005 0.0879182 12.2828 0.247423L15.6585 3.20119C15.8755 3.39108 16 3.66541 16 3.95377V11C16 12.1046 15.1046 13 14 13H12V5.95377C12 5.66541 11.8755 5.39108 11.6585 5.20119L8.28277 2.24742C8.10048 2.08792 7.86649 2 7.62426 2ZM12 1L15.5 4H14H13C12.4477 4 12 3.55228 12 3V1Z"
  }), /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M9 16H2C0.895431 16 0 15.1046 0 14V5C0 3.89543 0.895431 3 2 3H6.62426C6.86649 3 7.10048 3.08792 7.28277 3.24742L10.6585 6.20119C10.8755 6.39108 11 6.66541 11 6.95377V14C11 15.1046 10.1046 16 9 16ZM7 4L10.5 7H9H8C7.44772 7 7 6.55228 7 6V4Z"
  })),
  name: 'Duplicate',
  width: 16,
  height: 16
});
const DeleteIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero"
  }, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M2 3H14V14C14 15.1046 13.1046 16 12 16H4C2.89543 16 2 15.1046 2 14V3ZM6 7.5C6 7.22386 6.22386 7 6.5 7C6.77614 7 7 7.22386 7 7.5V12.5C7 12.7761 6.77614 13 6.5 13C6.22386 13 6 12.7761 6 12.5V7.5ZM9.5 7C9.22386 7 9 7.22386 9 7.5V12.5C9 12.7761 9.22386 13 9.5 13C9.77614 13 10 12.7761 10 12.5V7.5C10 7.22386 9.77614 7 9.5 7Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M10 0C10.5523 0 11 0.447715 11 1V2L5 2V1C5 0.447715 5.44772 0 6 0L10 0Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M15 3C15.5523 3 16 3.44772 16 4C16 4.55228 15.5523 5 15 5L0.999999 5C0.447715 5 0 4.55228 0 4C0 3.44772 0.447715 3 0.999999 3L15 3Z"
  })),
  name: 'Delete',
  width: 16,
  height: 16
});
const UnsortedIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", {
    stroke: "none",
    strokeWidth: "1"
  }, /*#__PURE__*/React.createElement("polygon", {
    className: "sort-ascending",
    points: "1.00055607 6 3.55271368e-15 7 3.00166821 10 6 7 5 6 3.00166821 8"
  }), /*#__PURE__*/React.createElement("polygon", {
    className: "sort-descending",
    transform: "translate(3.000000, 2.000000) scale(1, -1) translate(-3.000000, -2.000000) ",
    points: "1.00055607 -2.88472778e-15 3.55271368e-15 1 3.00166821 4 6 1 5 -6.21724894e-15 3.00166821 2"
  })),
  width: 6,
  height: 10,
  name: 'Unsorted'
});
const GridIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero"
  }, /*#__PURE__*/React.createElement("rect", {
    width: "7",
    height: "6",
    rx: "2"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "9",
    width: "7",
    height: "6",
    rx: "2"
  }), /*#__PURE__*/React.createElement("rect", {
    y: "8",
    width: "7",
    height: "6",
    rx: "2"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "9",
    y: "8",
    width: "7",
    height: "6",
    rx: "2"
  })),
  width: 16,
  height: 14,
  name: 'Grid'
});
const ListIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero"
  }, /*#__PURE__*/React.createElement("rect", {
    width: "16",
    height: "2",
    rx: "1"
  }), /*#__PURE__*/React.createElement("rect", {
    y: "4",
    width: "16",
    height: "2",
    rx: "1"
  }), /*#__PURE__*/React.createElement("rect", {
    y: "8",
    width: "16",
    height: "2",
    rx: "1"
  }), /*#__PURE__*/React.createElement("rect", {
    y: "12",
    width: "16",
    height: "2",
    rx: "1"
  })),
  width: 16,
  height: 14,
  name: 'List'
});
const RSSIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("path", {
    d: "M0.96759 6.42667C0.96759 7.17016 1.57657 7.75831 2.30456 7.90928C3.54149 8.1658 4.6779 8.77823 5.59095 9.6965C6.508 10.6171 7.11935 11.7644 7.37399 13.0138C7.52245 13.7422 8.11073 14.3509 8.85414 14.3509C9.59754 14.3509 10.2104 13.7448 10.1037 13.0091C9.51816 8.97082 6.33026 5.76847 2.30943 5.17812C1.57383 5.07012 0.96759 5.68319 0.96759 6.42667ZM0.971823 1.65416C0.971823 2.39757 1.57728 2.99175 2.31541 3.08014C7.47236 3.69766 11.574 7.82395 12.1849 13.0106C12.2719 13.7489 12.8662 14.3543 13.6096 14.3543C14.353 14.3543 14.9624 13.7498 14.8921 13.0097C14.2584 6.33716 8.95782 1.01181 2.31633 0.372498C1.57634 0.301267 0.971823 0.910756 0.971823 1.65416ZM4.69461 12.4736C4.69461 13.5031 3.85952 14.3382 2.82997 14.3382C1.80043 14.3382 0.965332 13.5034 0.965332 12.4736C0.965332 11.4434 1.80014 10.6089 2.82969 10.6089C3.85923 10.6089 4.69461 11.4434 4.69461 12.4736Z"
  }),
  width: 15,
  height: 15,
  name: 'RSS'
});
const DragIcon = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero"
  }, /*#__PURE__*/React.createElement("rect", {
    width: "3",
    height: "18"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "6",
    width: "3",
    height: "18"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "12",
    width: "3",
    height: "18"
  })),
  width: 15,
  height: 18,
  name: 'Drag'
});

const AcurisLogoWithText = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    className: "icon__acuris-logo-base",
    d: "M32.762 29.509l-10.286-5.642a1.432 1.432 0 0 0-1.379 0l-10.286 5.642-4.914 7.885 15.886-9.173 15.886 9.173-4.907-7.885z"
  }), /*#__PURE__*/React.createElement("path", {
    className: "icon__acuris-logo-main",
    d: "M20.046 1.143L.38 38.137a1.166 1.166 0 0 0 1.63 1.524l3.882-2.286L21.78 11.878l15.886 25.497 3.882 2.286a1.166 1.166 0 0 0 1.63-1.524L23.51 1.143a1.962 1.962 0 0 0-3.463 0z"
  }), /*#__PURE__*/React.createElement("path", {
    className: "icon__acuris-logo-text",
    d: "M50.766 32.404l9.95-24.857h3.81l9.95 24.857h-4.053l-2.636-6.934H57.5l-2.667 6.934h-4.068zM62.62 11.916a44.412 44.412 0 0 1-.83 2.393l-3.018 7.836h7.73l-3.017-7.836-.865-2.393zM89.253 26.023l2.808 1.802c-1.71 3.375-4.413 5.063-8.11 5.063a8.857 8.857 0 0 1-6.5-2.598c-1.744-1.735-2.617-3.965-2.617-6.69 0-2.725.89-4.95 2.667-6.674a8.872 8.872 0 0 1 6.45-2.617 9.269 9.269 0 0 1 4.952 1.264 7.326 7.326 0 0 1 2.998 3.52l-2.842 1.905c-1.11-2.242-2.797-3.364-5.063-3.364a5.238 5.238 0 0 0-3.9 1.612c-1.052 1.074-1.578 2.524-1.578 4.35 0 1.826.508 3.276 1.524 4.35a5.208 5.208 0 0 0 3.935 1.612 5.09 5.09 0 0 0 3.101-.884 8.038 8.038 0 0 0 2.175-2.651zM94.442 26.126V14.792h3.604v10.572c0 1.364.34 2.404 1.02 3.12.681.716 1.646 1.074 2.896 1.074a4.232 4.232 0 0 0 3.291-1.474 5.432 5.432 0 0 0 1.318-3.76V14.8h3.604v17.611h-3.604V29.57c-1.224 2.22-3.128 3.329-5.714 3.329a6.392 6.392 0 0 1-4.594-1.733c-1.214-1.163-1.821-2.843-1.821-5.04zM124.057 14.61l-.027 3.321a4.716 4.716 0 0 0-1.95-.32 4.099 4.099 0 0 0-3.154 1.635 6.004 6.004 0 0 0-1.25 3.9v9.258h-3.604V14.792h3.604v2.983c1.086-2.312 2.56-3.466 4.77-3.466.55.002 1.097.104 1.611.3zM136.785 19.048a1.97 1.97 0 0 0 1.093 1.733c1.035.543 2.154.906 3.31 1.074 2.032.417 3.528 1.04 4.488 1.87a4.305 4.305 0 0 1 1.44 3.43 5.253 5.253 0 0 1-1.836 4.083c-1.224 1.097-2.888 1.646-4.99 1.646a7.89 7.89 0 0 1-7.764-5.2l3.173-1.288c1.016 2.268 2.559 3.402 4.629 3.402a4.008 4.008 0 0 0 2.392-.693 2.122 2.122 0 0 0 .971-1.802c.01-.701-.362-1.352-.971-1.7-.648-.416-1.71-.762-3.189-1.04-3.997-.807-5.996-2.585-5.996-5.333a4.213 4.213 0 0 1 1.836-3.588 7.349 7.349 0 0 1 4.439-1.333c1.826 0 3.299.358 4.419 1.074a7.177 7.177 0 0 1 2.666 3.291l-2.85 1.143a5.01 5.01 0 0 0-1.607-1.912 4.32 4.32 0 0 0-2.564-.815 4.19 4.19 0 0 0-2.183.537 1.623 1.623 0 0 0-.906 1.42zM126.274 32.404V14.792h3.604v17.612h-3.604zm3.63-24.061h-3.61v3.611h3.61V8.343z"
  })),
  name: "AcurisLogoWithText",
  width: 148,
  height: 40
});
const AcurisTetraLogo = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero"
  }, /*#__PURE__*/React.createElement("path", {
    className: "icon__acuris-tetra-background",
    d: "M11.62112 10.38599l1.66956 8.2861c.06955.3461.31078.63358.63953.76216l7.8703 3.08307 6.61238-.9202-12.40788-4.54963-2.32592-13.00942-2.05797 6.34792z"
  }), /*#__PURE__*/React.createElement("path", {
    className: "icon__acuris-tetra-foreground",
    d: "M1.84933 30.5447l29.52302-6.27999c.3596-.08805.61914-.40118.63894-.77086.0198-.36968-.20482-.7087-.55296-.83459l-3.05903-1.08372L6.97146 24.6167l6.71585-20.57422-.53606-3.20074c-.06352-.36471-.35839-.64478-.7259-.68948-.3675-.0447-.72094.15653-.8701.49538L.24584 28.63372c-.1952.48433-.10746 1.0368.22817 1.43678.33563.39999.86447.58232 1.37533.4742z"
  })),
  name: "AcurisTetraLogo",
  width: 32,
  height: 32
});
const MergermarketTetraLogo = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", {
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M24.8575032,30.627995 L23.4394119,23.4922133 L7.91439552,7.43823854 C6.98955342,6.56083805 6.08937374,6.88296062 5.73176811,8.09782283 C5.48514358,8.93227367 0.121059269,26.8637628 0.121059269,26.8637628 L5.42348744,20.544025 L7.45505729,10.9693154 L24.8575032,30.627995 Z",
    fill: "#336489"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M20.7214685,10.0253094 L24.8096207,30.623132 L25.2070372,31.0800435 C25.7492488,31.6933477 26.1312615,32 26.7166035,32 C27.3882065,32 28.1060207,31.1720392 28.041325,30.4023425 C27.9766293,29.6326456 26.3315101,4.77235871 26.2544914,3.88000105 C26.100454,2.08301967 25.8971246,0.733750363 24.8681549,0.24310698 C23.6358558,-0.321132911 22.5760786,0.144978303 21.6826618,1.1446642 C20.9987358,1.92356057 0.102024203,26.8666436 0.102024203,26.8666436 C-0.178323837,28.0932521 0.135912429,29.0714723 0.838322902,29.4915857 C1.54073338,29.9116991 2.53273413,29.7982378 3.72190273,28.4367024 L20.7214685,10.0253094 Z",
    fill: "#60B2C4"
  })),
  name: "MergermarketTetraLogo",
  width: 32,
  height: 32
});

const IconTextButton = styled.button`
  display: inline;
  background: transparent;
  outline: none;
  border: none;
  cursor: pointer;
  padding: 0;

  color: ${divisionColors.medium};

  & > :first-child {
    margin-right: 0.4rem;
  }
`;
const getWrapperSelector = ({
  pseudo
}) => `${IconTextButton}${pseudo ? `:${pseudo}` : ''} > &`;
const IconText = styled.span`
  ${getWrapperSelector({
  pseudo: 'hover'
})} {
    text-decoration: underline;
  }
`;

const FollowBellIconSVG = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", {
    stroke: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("path", {
    className: "bell-inner",
    d: "M2.55170615,10.7528805 C3.09142583,9.99572956 3.34258062,9.14750176 3.69236355,7.27289942 C4.19386283,4.59038314 4.54240952,3.61518064 5.74313623,2.91819333 C6.95895152,2.21337288 8.39104863,2.2231784 9.59135645,2.91847913 C10.7722493,3.60395364 11.1432134,4.61451009 11.6196119,7.15871478 C11.6303047,7.21585087 11.6303047,7.21585087 11.6409415,7.27274829 C11.7667386,7.94506389 11.8275107,8.2483045 11.9165685,8.61946464 C12.1379135,9.54194858 12.4075827,10.2276549 12.7820631,10.7525569 L13.9098353,12.3333333 L1.42511235,12.3333333 L2.55170615,10.7528805 Z M7.66666667,0.333333333 C8.21866667,0.333333333 8.66666667,0.782333333 8.66666667,1.33333333 C8.66666667,1.88533333 8.21866667,2.33333333 7.66666667,2.33333333 C7.11466667,2.33333333 6.66666667,1.88533333 6.66666667,1.33333333 C6.66666667,0.782333333 7.11466667,0.333333333 7.66666667,0.333333333 Z",
    fillRule: "nonzero",
    opacity: "0"
  }), /*#__PURE__*/React.createElement("path", {
    className: "bell-outline",
    d: "M7.66666667,0.666666667 C8.03466667,0.666666667 8.33333333,0.966 8.33333333,1.33333333 C8.33333333,1.70133333 8.03466667,2 7.66666667,2 C7.29866667,2 7,1.70133333 7,1.33333333 C7,0.966 7.29866667,0.666666667 7.66666667,0.666666667 Z M3.366,11.3333333 C4.15866667,10.2213333 4.436,8.73933333 4.67533333,7.45666667 C4.97333333,5.86266667 5.25466667,4.358 6.24466667,3.78333333 C7.16466667,3.25 8.21466667,3.27666667 9.08933333,3.78333333 C10.0793333,4.358 10.36,5.86266667 10.658,7.45666667 C10.898,8.73933333 11.1746667,10.2213333 11.968,11.3333333 L3.366,11.3333333 Z M9.758,2.63 C12.8713333,4.43533333 11.082,10.44 14.3333333,11.5013333 L14.3333333,12.6666667 L1,12.6666667 L1,11.5013333 C4.25133333,10.44 2.46266667,4.436 5.57533333,2.63 C6.00466667,2.38066667 6.27,1.91733333 6.26866667,1.41466667 C6.26866667,0.633333333 6.89466667,0 7.66666667,0 C8.438,0 9.06466667,0.633333333 9.06466667,1.41666667 C9.06333333,1.91666667 9.32866667,2.38133333 9.758,2.63 Z M9.66666667,14 C9.66666667,15.0653333 8.738,16 7.686,16 C6.63333333,16 5.66666667,15.0653333 5.66666667,14 L9.66666667,14 Z"
  })),
  width: 16,
  height: 16,
  name: 'FollowBell'
});
const FollowBellIcon = styled(FollowBellIconSVG)`
  & .bell-inner {
    fill: inherit;
    opacity: ${props => props.following ? '100' : '0'};
  }
  & .bell-outline {
    fill: inherit;
  }

  fill: ${warmGrey.VIII};

  ${getWrapperSelector({
  pseudo: 'hover'
})},
  &:hover {
    fill: ${props => props.following ? warmGrey.X : warmGrey.VI};
    & .bell-inner {
      opacity: 100;
    }
  }
`;
const SortIconSVG = makeIconComponent({
  content: /*#__PURE__*/React.createElement("g", {
    stroke: "none",
    strokeWidth: "1"
  }, /*#__PURE__*/React.createElement("polygon", {
    className: "sort-ascending",
    points: "1.00055607 6 3.55271368e-15 7 3.00166821 10 6 7 5 6 3.00166821 8"
  }), /*#__PURE__*/React.createElement("polygon", {
    className: "sort-descending",
    transform: "translate(3.000000, 2.000000) scale(1, -1) translate(-3.000000, -2.000000) ",
    points: "1.00055607 -2.88472778e-15 3.55271368e-15 1 3.00166821 4 6 1 5 -6.21724894e-15 3.00166821 2"
  })),
  width: 6,
  height: 10,
  name: 'Sort'
});
const SortIcon = styled(SortIconSVG)`
  height: 1.4rem;
  polygon.sort-ascending {
    width: 0.6rem;
    height: 0.4rem;
    fill: ${props => props.sortDirection === 'descending' ? warmGrey.VI : warmGrey.X};
    position: absolute;
    top: 0.5rem;
    left: 0;
  }
  polygon.sort-descending {
    width: 0.6rem;
    height: 0.4rem;
    fill: ${props => props.sortDirection === 'ascending' ? warmGrey.VI : warmGrey.X};
    position: absolute;
    left: 0;
    top: 1rem;
  }
`;

export { AcurisLogoWithText, AcurisTetraLogo, ArrowIcon, BellIcon, BreadcrumbChevronIcon, CalendarIcon, ChevronIcon, CrossIcon, DashIcon, DeleteIcon, DragIcon, DuplicateIcon, EmailIcon, FollowBellIcon, GridIcon, HamburgerIcon, HomeIcon, IconText, IconTextButton, InfoIcon, InverseEmailIcon, LinkedInIcon, ListIcon, MediumArrowIcon, MergermarketTetraLogo, NarrowCrossIcon, PrintIcon, RSSIcon, ScrollTopIcon, SearchIcon, ShareIcon, SortIcon, ThinArrowIcon, TickIcon, UnsortedIcon, UserIcon, XingIcon };
//# sourceMappingURL=index.js.map
